import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Student } from '../types';
import { ClipboardListIcon, PlayIcon, CheckBadgeIcon, ClockIcon, PencilSquareIcon } from './icons';

interface ClassAssignmentsViewProps {
    student: Student;
    onStartQuiz: (quizId: string) => void;
    onOpenOptical: (quizId: string) => void;
}

export const ClassAssignmentsView: React.FC<ClassAssignmentsViewProps> = ({
    student,
    onStartQuiz,
    onOpenOptical
}) => {
    const [assignments, setAssignments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAssignments = async () => {
            if (!student.assignedTeacherIds || student.assignedTeacherIds.length === 0) {
                setLoading(false);
                return;
            }

            try {
                // Since a student might belong to multiple teacher's classes theoretically, 
                // but usually one primary class, we fetch for all assigned teachers or use className.
                // The most reliable way here is to use the class they just joined.

                // For now, let's fetch based on the first teacher and class name logic 
                // or just pull all assignments for this student if we had an endpoint.
                // Since we have api.getAssignedQuizzes(teacherId, classId), we need classId.

                // Let's assume we need to find the class first or the student has it.
                // I'll fetch classes for the teachers assigned to the student.

                const allFetchedAssignments: any[] = [];

                for (const tId of student.assignedTeacherIds) {
                    // This is a bit heavy, in a real app we'd have a student-specific endpoint
                    // But for this architecture, we'll try to find the match.
                    try {
                        const teacherClasses = await api.getClasses(tId);
                        const myClass = teacherClasses.find((c: any) => c.studentIds.includes(student.id));

                        if (myClass) {
                            const classQuizzes = await api.getAssignedQuizzes(tId, myClass.id);
                            allFetchedAssignments.push(...classQuizzes);
                        }
                    } catch (e) {
                        console.error(`Error fetching for teacher ${tId}:`, e);
                    }
                }

                setAssignments(allFetchedAssignments);
            } catch (err) {
                setError('Testler yüklenirken bir hata oluştu.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAssignments();
    }, [student.id, student.assignedTeacherIds]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-2">Sınıf Testlerim</h2>
                    <p className="text-indigo-100 opacity-90">Öğretmeniniz tarafından atanan tüm testlere buradan ulaşabilirsiniz.</p>
                </div>
            </div>

            {assignments.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-3xl p-12 text-center border-2 border-dashed border-gray-200 dark:border-slate-700">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Henüz Atanmış Test Yok</h3>
                    <p className="text-gray-500 dark:text-gray-400">Öğretmeniniz test atadığında burada görünecektir.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {assignments.map((quiz, index) => (
                        <div key={quiz.id || index} className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-slate-700 hover:shadow-xl transition-all group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl text-indigo-600 dark:text-indigo-400">
                                    <ClipboardListIcon className="w-6 h-6" />
                                </div>
                                <span className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs font-bold rounded-full">
                                    {quiz.examType || 'TYT'}
                                </span>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-indigo-600 transition-colors">
                                {quiz.topic}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{quiz.subject}</p>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                    <ClockIcon className="w-4 h-4" />
                                    <span>Atanma Tarihi: {quiz.date || 'Belirtilmedi'}</span>
                                </div>
                                {quiz.studentCount !== undefined && (
                                    <div className="flex items-center gap-2 text-xs text-green-500">
                                        <CheckBadgeIcon className="w-4 h-4" />
                                        <span>{quiz.studentCount} Öğrenci Tamamladı</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => onStartQuiz(quiz.quizId)}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all active:scale-95"
                                >
                                    <PlayIcon className="w-4 h-4" />
                                    Testi Çöz
                                </button>
                                <button
                                    onClick={() => onOpenOptical(quiz.quizId)}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 rounded-xl font-bold text-sm transition-all active:scale-95"
                                >
                                    <PencilSquareIcon className="w-4 h-4" />
                                    Sanal Optik
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
